import * as d3 from "d3";
import NotificationHelper from "services/notification-helper";
import {
  LEGEND_FONT_FAMILY,
  LEGEND_FONT_SIZE,
  LEGEND_LINE_HEIGHT,
  LEGEND_SWATCH_GAP,
  LEGEND_SWATCH_SIZE,
  layoutLegend,
} from "utilities/chartLegendLayout";

function toBase64Unicode(str: string) {
  // Safe Unicode → Base64 conversion
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )
  );
}

export function downloadChartImage({
  svgRef,
  format,
  fileName,
  title,
  subtitle,
  legendItems,
  legendConfig,
  titlePosition = "top",
  scale = 2,
  onError,
}: {
  svgRef: React.RefObject<SVGSVGElement>;
  format: "png" | "jpeg";
  fileName: string;
  title?: string;
  subtitle?: string;
  legendItems?: Array<{ color: string; label: string }>;
  legendConfig?: {
    marginLeft?: number;
    marginTop?: number;
    itemWidth?: number;
    itemHeight?: number;
    padding?: number;
  };
  titlePosition?: "top" | "bottom";
  /** Raster upscale factor, so exports stay legible at report size. */
  scale?: number;
  onError?: (err?: Error) => void;
}) {
  if (!svgRef.current) return;

  // Clone SVG
  const clonedSvg = svgRef.current.cloneNode(true) as SVGSVGElement;
  const svgCloneSelection = d3.select(clonedSvg);

  // Chart dimensions / legend config
  const marginLeft = legendConfig?.marginLeft ?? 40;
  const marginTop = legendConfig?.marginTop;
  const itemWidth = legendConfig?.itemWidth ?? 180;
  const itemHeight = legendConfig?.itemHeight ?? 20;
  const legendPadding = legendConfig?.padding ?? 10;

  // A viewBox is the chart's own coordinate system, so it is the only source
  // that survives CSS scaling (`max-width: 100%`). Client sizes are a fallback
  // for charts that have not been migrated to a viewBox yet.
  const viewBox = svgRef.current
    .getAttribute("viewBox")
    ?.split(/[\s,]+/)
    .map(Number)
    .filter((value) => Number.isFinite(value));
  const hasViewBox = viewBox?.length === 4 && viewBox[2] > 0 && viewBox[3] > 0;

  const width = hasViewBox
    ? (viewBox as number[])[2]
    : svgRef.current.clientWidth ||
      parseInt(svgRef.current.getAttribute("width") ?? "0", 10) ||
      1100;
  const height = hasViewBox
    ? (viewBox as number[])[3]
    : svgRef.current.clientHeight ||
      parseInt(svgRef.current.getAttribute("height") ?? "0", 10) ||
      500;

  const desiredLeftMargin = 100; // Match your on-screen chart's margin.left

  // --- DYNAMIC LEGEND CALC ---
  let legendHeight = 0;
  let legendTop = 0;
  let legendLayout = layoutLegend([], itemWidth);
  if (legendItems && legendItems.length > 0) {
    const availableLegendWidth = Math.max(itemWidth, width - marginLeft * 2);

    if (marginTop !== undefined) {
      legendTop = marginTop;
    } else if (titlePosition === "top") {
      legendTop = subtitle ? 72 : 40;
    } else {
      legendTop = 10;
    }

    legendLayout = layoutLegend(legendItems, availableLegendWidth);
    legendHeight = Math.max(itemHeight, legendLayout.height) + legendPadding;
  }

  // --- TITLE ---
  if (title) {
    let titleY;
    if (titlePosition === "bottom") {
      // Place title below the chart area, with padding
      titleY = height + legendHeight + legendTop - 16;
    } else {
      // Default: place title at top
      titleY = 30;
    }
    svgCloneSelection
      .append("text")
      .attr("x", width / 2)
      .attr("y", titleY)
      .attr("text-anchor", "middle")
      .attr("class", "chart-title-download")
      .text(title)
      .style("font-size", "24px")
      .style("font-weight", "bold")
      .style("fill", "#05004E");
  }

  // --- SUBTITLE ---
  if (subtitle && titlePosition !== "bottom") {
    svgCloneSelection
      .append("text")
      .attr("x", width / 2)
      .attr("y", 62)
      .attr("text-anchor", "middle")
      .attr("class", "chart-subtitle-download")
      .text(subtitle)
      .style("font-size", "16px")
      .style("font-weight", "normal")
      .style("fill", "#425466");
  }

  // --- LEGEND ---
  if (legendItems && legendItems.length > 0) {
    svgCloneSelection.selectAll(".legend-download").remove();
    const legendGroup = svgCloneSelection
      .append("g")
      .attr("class", "legend-download")
      .attr("transform", `translate(${marginLeft}, ${legendTop})`);

    legendLayout.entries.forEach((item) => {
      legendGroup
        .append("rect")
        .attr("x", item.x)
        .attr("y", item.y)
        .attr("width", LEGEND_SWATCH_SIZE)
        .attr("height", LEGEND_SWATCH_SIZE)
        .attr("fill", item.color)
        .attr("rx", 2)
        .attr("ry", 2);

      const legendText = legendGroup
        .append("text")
        .attr("x", item.x + LEGEND_SWATCH_SIZE + LEGEND_SWATCH_GAP)
        .attr("y", item.y + LEGEND_LINE_HEIGHT - 4)
        .style("font-size", LEGEND_FONT_SIZE)
        .style("fill", "#425466")
        .style("font-family", LEGEND_FONT_FAMILY);

      item.lines.forEach((line, lineIndex) => {
        legendText
          .append("tspan")
          .attr("x", item.x + LEGEND_SWATCH_SIZE + LEGEND_SWATCH_GAP)
          .attr("dy", lineIndex === 0 ? 0 : LEGEND_LINE_HEIGHT)
          .text(line);
      });
    });
  }

  // --- SHIFT CHART GROUP DOWN ---
  // Only when this helper has injected a title/legend above the chart. An SVG
  // that already contains its own decorations is exported untouched.
  const verticalOffset = legendHeight + legendTop;
  const chartGroup = svgCloneSelection.select("g");
  if (verticalOffset > 0 && !chartGroup.empty()) {
    chartGroup.attr(
      "transform",
      `translate(${desiredLeftMargin}, ${verticalOffset})`
    );
  }

  // --- Adjust SVG size for download ---
  const totalHeight = height + legendHeight + legendTop;
  // Upscaling is only safe when the chart declares its own coordinate system:
  // enlarging width/height without a viewBox would grow the canvas but not the
  // drawing. Charts without one are exported at their natural size, as before.
  const rasterScale = hasViewBox ? Math.max(1, scale) : 1;
  if (hasViewBox) {
    clonedSvg.setAttribute("viewBox", `0 0 ${width} ${totalHeight}`);
    clonedSvg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    clonedSvg.setAttribute("width", `${width * rasterScale}`);
  }
  clonedSvg.setAttribute("height", `${totalHeight * rasterScale}`);

  // --- Export ---
  try {
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clonedSvg);
    const svgBase64 = `data:image/svg+xml;base64,${toBase64Unicode(svgString)}`;
    const img = new Image();
    img.src = svgBase64;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * rasterScale;
      canvas.height = totalHeight * rasterScale;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }
          },
          `image/${format}`
        );
      }
    };

    img.onerror = (ev) => {
      const err = new Error("Unable to load generated image for export.");
      if (onError) onError(err);
      else NotificationHelper.notifyError("Unable to export chart. Please try again.");
    };
  } catch (err) {
    if (onError) onError(err as Error);
    else NotificationHelper.notifyError("Unable to export chart. Please try again.");
  }
}